/**
 * DynamicPageBanner - Fetches and displays banners from admin panel
 * Supports image, video, and gradient backgrounds
 * 
 * Optie 5 Implementation:
 * - Poster loads instantly (small JPG)
 * - Video loads lazily after page load
 * - WebM primary, MP4 fallback for Safari
 * 
 * Usage: <DynamicPageBanner page="refer-a-friend" />
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const DynamicPageBanner = ({ page, fallbackContent = null }) => {
  const { i18n } = useTranslation();
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${API}/page-banners/${page}`);
        if (response.data.banner) {
          setBanner(response.data.banner);
        }
      } catch (error) {
        console.error('Failed to fetch banner:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanner();
  }, [page]);

  // Lazy load video after page is ready
  useEffect(() => {
    if (!banner) return;
    
    const hasVideo = banner.background_type === 'video' && (banner.video_url || banner.video_url_mp4);
    const lazyLoad = banner.lazy_load !== false; // Default true
    
    if (hasVideo) {
      if (lazyLoad) {
        // Wait for page to be fully loaded, then load video
        const timer = setTimeout(() => {
          setShouldLoadVideo(true);
        }, 100); // Small delay to ensure poster is visible first
        return () => clearTimeout(timer);
      } else {
        setShouldLoadVideo(true);
      }
    }
  }, [banner]);

  // Don't render anything while loading or if no banner
  if (loading) return null;
  if (!banner || !banner.enabled) return fallbackContent;

  // Get translation for current language
  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const translation = banner.translations?.[currentLang] || banner.translations?.en || {};
  const title = translation.title || '';
  const subtitle = translation.subtitle || '';

  // Determine background type
  const backgroundType = banner.background_type || 'image';
  const hasVideo = backgroundType === 'video' && (banner.video_url || banner.video_url_mp4);
  
  // Poster: prefer poster_path, fallback to image_url
  const posterUrl = banner.poster_path || banner.image_url;
  const hasImage = banner.image_url;

  // Video URLs
  const videoWebm = banner.video_url; // Primary (WebM)
  const videoMp4 = banner.video_url_mp4 || banner.video_url; // Fallback (MP4)

  // Video settings
  const autoplay = banner.autoplay !== false; // Default true
  const loop = banner.loop !== false; // Default true
  const muted = banner.muted !== false; // Default true

  // Don't show if no content and no background
  if (!title && !subtitle && !hasImage && !hasVideo && !posterUrl) return fallbackContent;

  const gradientOpacity = (banner.gradient_opacity ?? 85) / 100;
  const bannerHeight = banner.height || 120;

  return (
    <div 
      className="text-white flex items-center justify-center relative overflow-hidden w-full"
      style={{ 
        height: `${bannerHeight}px`,
        width: '100%',
      }}
      data-testid={`page-banner-${page}`}
    >
      {/* Poster Image - Shows instantly, stays until video is ready */}
      {(hasVideo || hasImage) && posterUrl && (
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${posterUrl})`,
            backgroundSize: '100% auto',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: videoLoaded ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
          }}
        />
      )}

      {/* Video Background - Lazy loaded with WebM + MP4 fallback */}
      {hasVideo && shouldLoadVideo && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster={posterUrl}
          onCanPlayThrough={() => setVideoLoaded(true)}
          onLoadedData={() => {
            // Start playing as soon as data is available
            if (videoRef.current && autoplay) {
              videoRef.current.play().catch(() => {});
            }
          }}
          onEnded={(e) => {
            // Fallback loop mechanism for browsers that don't respect loop attribute
            e.target.currentTime = 0;
            e.target.play().catch(() => {});
          }}
          onTimeUpdate={(e) => {
            // Extra safety: restart if near end and loop is enabled
            const video = e.target;
            if (video.duration && video.currentTime >= video.duration - 0.1) {
              video.currentTime = 0;
            }
          }}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 0.5s ease-out',
            transform: 'translateZ(0)',
          }}
        >
          {/* WebM source (primary - better compression) */}
          {videoWebm && videoWebm.endsWith('.webm') && (
            <source src={videoWebm} type="video/webm" />
          )}
          {/* MP4 source (fallback - Safari/older browsers) */}
          {videoMp4 && (
            <source src={videoMp4} type="video/mp4" />
          )}
          Your browser does not support the video tag.
        </video>
      )}

      {/* Image Background - only show if not using video */}
      {!hasVideo && hasImage && !posterUrl && (
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${banner.image_url})`,
            backgroundSize: '100% auto',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '100%',
          }}
        />
      )}

      {/* Gradient Overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600"
        style={{ 
          opacity: gradientOpacity
        }}
      />

      {/* Content */}
      <div className="max-w-6xl mx-auto text-center py-4 relative z-10 px-4">
        {title && (
          <h2 className="font-bold text-xl md:text-2xl mb-1">{title}</h2>
        )}
        {subtitle && (
          <p className="text-sm md:text-base opacity-90">{subtitle}</p>
        )}
      </div>
    </div>
  );
};

export default DynamicPageBanner;
